import { Injectable } from '@angular/core';
import { getAnnotationCategory } from 'src/app/model/thingie/annotation';
import { IThingie } from 'src/app/model/thingie/thingie';
import {
  ExperimentAnnotationsContext,
  GraphAgentAnnotationCategory,
  GraphAgentAnnotationEntry
} from 'src/app/services/chat/chat.service';
import { ThingieHelper } from 'src/app/utility/thingie-helper';

export const MAX_ANNOTATION_LABEL_CHARS = 500;

@Injectable({ providedIn: 'root' })
export class ExperimentAnnotationsContextService {
  public buildFromThingies(
    thingies: IThingie[],
    options: {
      experimentName: string;
      experimentGroupId: string;
    }
  ): ExperimentAnnotationsContext {
    const sourceThingies = uniqueThingies(thingies);
    const sourceThingieIds = sourceThingies
      .map(thingie => thingie._id?.trim() ?? '')
      .filter(Boolean);
    const allEntries = sourceThingies
      .flatMap(thingie => collectEntriesForThingie(thingie))
      .sort(compareAnnotationEntries);
    const entries = allEntries.map(entry => ({
      ...entry,
      label: truncateAnnotationLabel(entry.label)
    }));

    return {
      manifest: {
        experimentGroupId: options.experimentGroupId,
        experimentName: options.experimentName,
        sourceThingieIds,
        replicateCount: sourceThingieIds.length,
        annotationCount: entries.length,
        totalAnnotationCount: allEntries.length
      },
      entries
    };
  }
}

export function serializeExperimentAnnotationsContext(
  context?: ExperimentAnnotationsContext
): ExperimentAnnotationsContext | undefined {
  if (!context) {
    return undefined;
  }

  const sourceThingieIds = (context.manifest.sourceThingieIds ?? [])
    .map(thingieId => thingieId.trim())
    .filter(Boolean);
  const entries = (context.entries ?? [])
    .map(entry => ({
      thingieId: entry.thingieId.trim(),
      replicateName: entry.replicateName.trim(),
      processIndex: entry.processIndex,
      timestamp: entry.timestamp.trim(),
      category: entry.category,
      label: truncateAnnotationLabel(entry.label.trim()),
      hasAttachments: entry.hasAttachments === true ? true : undefined
    }))
    .filter(entry =>
      Boolean(entry.thingieId)
      && Boolean(entry.replicateName)
      && Boolean(entry.timestamp)
      && isAnnotationCategory(entry.category)
      && Boolean(entry.label));

  if (!context.manifest.experimentGroupId.trim()
    || !context.manifest.experimentName.trim()
    || sourceThingieIds.length === 0) {
    return undefined;
  }

  return {
    manifest: {
      experimentGroupId: context.manifest.experimentGroupId.trim(),
      experimentName: context.manifest.experimentName.trim(),
      sourceThingieIds,
      replicateCount: context.manifest.replicateCount,
      annotationCount: entries.length,
      totalAnnotationCount: context.manifest.totalAnnotationCount,
      truncated: entries.length < context.manifest.totalAnnotationCount ? true : undefined
    },
    entries
  };
}

export function truncateAnnotationLabel(label: string): string {
  const normalized = label.replace(/\s+/g, ' ').trim();
  if (normalized.length <= MAX_ANNOTATION_LABEL_CHARS) {
    return normalized;
  }

  return `${normalized.slice(0, MAX_ANNOTATION_LABEL_CHARS - 3)}...`;
}

export function compareAnnotationEntries(
  left: GraphAgentAnnotationEntry,
  right: GraphAgentAnnotationEntry
): number {
  const timestampComparison = Date.parse(right.timestamp) - Date.parse(left.timestamp);
  if (!Number.isNaN(timestampComparison) && timestampComparison !== 0) {
    return timestampComparison;
  }

  const replicateComparison = left.replicateName.localeCompare(right.replicateName);
  if (replicateComparison !== 0) {
    return replicateComparison;
  }

  return left.processIndex - right.processIndex;
}

function collectEntriesForThingie(thingie: IThingie): GraphAgentAnnotationEntry[] {
  const thingieId = thingie._id?.trim() ?? '';
  if (!thingieId) {
    return [];
  }

  const replicateName = ThingieHelper.getReplicateFullname(thingie);
  return thingie.processes.flatMap((process, processIndex) =>
    (process.annotations ?? [])
      .filter(annotation =>
        annotation.deleted !== true
        && annotation.hidden !== true
        && getAnnotationCategory(annotation) !== 'copilot_conversation'
        && Boolean(annotation.label?.trim()))
      .map(annotation => ({
        thingieId,
        replicateName,
        processIndex,
        timestamp: annotation.timestamp,
        category: getAnnotationCategory(annotation) as GraphAgentAnnotationCategory,
        label: annotation.label,
        hasAttachments: (annotation.files ?? []).some(file => file.deleted !== true) || undefined
      })));
}

function uniqueThingies(thingies: IThingie[]): IThingie[] {
  const seen = new Set<string>();
  return thingies.filter(thingie => {
    const thingieId = thingie._id?.trim() ?? '';
    if (!thingieId || seen.has(thingieId)) {
      return false;
    }
    seen.add(thingieId);
    return true;
  });
}

function isAnnotationCategory(category: string): category is GraphAgentAnnotationCategory {
  return category === 'user' || category === 'system';
}
